/**
 * Component responsible for initializing the configuration store.
 * This component should be rendered near the root of the application
 * to ensure the configuration is available throughout the app.
 */

'use client';

import { useConfigStore } from '@/stores/configStore';
import type { SystemConfig } from '@/config/serverConfig';

/**
 * Props for the ConfigStoreInitializer component
 */
interface ConfigStoreInitializerProps {
  /** System configuration to initialize the store with */
  config: SystemConfig;
}

/**
 * Initializes the configuration store with the provided config.
 * Only initializes once, subsequent renders with different configs will not update the store.
 *
 * Runs synchronously on render (not after `useEffect`) so {@link SystemConfig.basePath}
 * matches `window.__LIST_STORAGE_BASE_PATH__` before Zustand `persist` rehydrates descendants.
 *
 * @example
 * <ConfigStoreInitializer config={systemConfig} />
 */
export function ConfigStoreInitializer({ config }: ConfigStoreInitializerProps) {
  const isInitialized = useConfigStore((state) => state.isInitialized);
  if (!isInitialized) {
    useConfigStore.getState().setSystemConfig(config);
  }

  return null;
}
